"use client";

import { useEffect, useState } from "react";
import Button from "@/app/components/Button";
import { useGateHold } from "@/app/components/Gate";

const WORLD = "Baunheim";

type Kind = "scenario" | "rules" | "taboo";

/** タブの名と、ヘッダの 2 行目に出す説明 */
const TABS: { kind: Kind; label: string; lead: string }[] = [
  { kind: "scenario", label: "脚本設定", lead: "物語の味付け。保存すると、次の進行から効きます。" },
  { kind: "rules", label: "執筆規定", lead: "散文を書くときの決まりで、追加の規定は次の進行から効きます。" },
  { kind: "taboo", label: "禁句設定", lead: "散文に出さない言葉で、次の進行から効きます。" },
];

/** 脚本設定の 9 項目。両端の言葉は創作基盤の基本設計書の表 */
const ITEMS: { key: string; low: string; high: string }[] = [
  { key: "シリアス度", low: "コメディ", high: "重厚シリアス" },
  { key: "ダーク度", low: "ほのぼの", high: "残酷・陰鬱" },
  { key: "表現強度", low: "マイルド", high: "過激・容赦なし" },
  { key: "台詞比率", low: "情景描写重視", high: "台詞・会話重視" },
  { key: "文章難度", low: "平易な言葉だけ", high: "難しい語彙" },
  { key: "テンポ", low: "じっくり", high: "急展開" },
  { key: "事件発生率", low: "日常多め・安全", high: "事件・危機の連続" },
  { key: "NPC交流度", low: "ソロ中心", high: "NPC多め" },
  { key: "ご都合展開率", low: "奇跡なし", high: "都合よく助かる" },
];

/** scenario.json の中身。9 項目は 1〜5、テーマは文字列 */
type Scenario = Record<string, number | string>;
const LEVELS = [1, 2, 3, 4, 5];
const THEME_MAX = 100;

/** 執筆規定。共通の規定は節ごとの行、追加の規定はそのキャラの並び */
interface Rules {
  common: { title: string; lines: string[] }[];
  extra: string[];
  limits: { count: number; length: number };
}

/** 禁句。そのキャラの言葉の並び */
interface Taboo {
  words: string[];
  limits: { count: number; length: number };
}

/**
 * 脚本設定の画面。脚本設定・執筆規定・禁句設定 の 3 つを並べる。
 * 脚本設定は scenario.json、執筆規定の追加の規定は rules.json、禁句は taboo.json を読み書きする
 */
export default function PlayScenario({ region, slug, onBack }: { region: string; slug: string; onBack: () => void }) {
  const [kind, setKind] = useState<Kind>("scenario");
  const [saved, setSaved] = useState<Scenario | null>(null);
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let alive = true;
    fetch(`/api/play/scenario?world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(slug)}`)
      .then(async (response) => {
        const body = await response.json();
        if (!alive) return;
        if (response.ok) {
          setSaved(body.scenario as Scenario);
          setScenario(body.scenario as Scenario);
        } else setMessage(body.error ?? "脚本設定を読めません");
      })
      .catch(() => {
        if (alive) setMessage("サーバーに繋がりません");
      });
    return () => {
      alive = false;
    };
  }, [region, slug]);

  const changed = scenario !== null && saved !== null && Object.keys(scenario).some((key) => scenario[key] !== saved[key]);

  const change = (key: string, value: number | string) => {
    setScenario((now) => (now ? { ...now, [key]: value } : now));
    setMessage("");
  };

  const save = async () => {
    if (!scenario || sending) return;
    setSending(true);
    setMessage("");
    try {
      const response = await fetch("/api/play/scenario_save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ world: WORLD, region, slug, scenario }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error ?? "保存できません");
      setSaved(body.scenario as Scenario);
      setScenario(body.scenario as Scenario);
      setMessage("保存しました。次の進行から効きます。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "保存できません");
    } finally {
      setSending(false);
    }
  };

  const [rules, setRules] = useState<Rules | null>(null);
  const [commonOpen, setCommonOpen] = useState(false);
  const [rulesSending, setRulesSending] = useState(false);
  const [rulesMessage, setRulesMessage] = useState("");
  const [extraDraft, setExtraDraft] = useState("");

  useEffect(() => {
    let alive = true;
    fetch(`/api/play/rules?world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(slug)}`)
      .then(async (response) => {
        const body = await response.json();
        if (!alive) return;
        if (response.ok) setRules(body as Rules);
        else setRulesMessage(body.error ?? "執筆規定を読めません");
      })
      .catch(() => {
        if (alive) setRulesMessage("サーバーに繋がりません");
      });
    return () => {
      alive = false;
    };
  }, [region, slug]);

  /** 追加の規定の並びを丸ごと保存する。足す・消すのたびに呼ぶ */
  const saveExtra = async (next: string[]): Promise<boolean> => {
    if (rulesSending) return false;
    setRulesSending(true);
    setRulesMessage("");
    try {
      const response = await fetch("/api/play/rules_save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ world: WORLD, region, slug, rules: next }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error ?? "保存できません");
      setRules((now) => (now ? { ...now, extra: body.extra as string[] } : now));
      return true;
    } catch (error) {
      setRulesMessage(error instanceof Error ? error.message : "保存できません");
      return false;
    } finally {
      setRulesSending(false);
    }
  };

  const addExtra = async () => {
    const text = extraDraft.trim();
    if (text === "" || !rules) return;
    if (await saveExtra([...rules.extra, text])) setExtraDraft("");
  };

  const removeExtra = (index: number) => {
    if (rules) saveExtra(rules.extra.filter((_, at) => at !== index));
  };

  const [words, setWords] = useState<Taboo | null>(null);
  const [wordsSending, setWordsSending] = useState(false);
  const [wordsMessage, setWordsMessage] = useState("");
  const [wordDraft, setWordDraft] = useState("");
  // 脚本設定・執筆規定・禁句の 3 つを読み終えるまで、ロード画面に待ってもらう
  useGateHold((scenario === null && message === "") || (rules === null && rulesMessage === "") || (words === null && wordsMessage === ""));

  useEffect(() => {
    let alive = true;
    fetch(`/api/play/taboo?world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(slug)}`)
      .then(async (response) => {
        const body = await response.json();
        if (!alive) return;
        if (response.ok) setWords(body as Taboo);
        else setWordsMessage(body.error ?? "禁句を読めません");
      })
      .catch(() => {
        if (alive) setWordsMessage("サーバーに繋がりません");
      });
    return () => {
      alive = false;
    };
  }, [region, slug]);

  /** 禁句の並びを丸ごと保存する。足す・消すのたびに呼ぶ */
  const saveWords = async (next: string[]): Promise<boolean> => {
    if (wordsSending) return false;
    setWordsSending(true);
    setWordsMessage("");
    try {
      const response = await fetch("/api/play/taboo_save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ world: WORLD, region, slug, words: next }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error ?? "保存できません");
      setWords((now) => (now ? { ...now, words: body.words as string[] } : now));
      return true;
    } catch (error) {
      setWordsMessage(error instanceof Error ? error.message : "保存できません");
      return false;
    } finally {
      setWordsSending(false);
    }
  };

  const addWord = async () => {
    const word = wordDraft.trim();
    if (word === "" || !words) return;
    if (words.words.includes(word)) {
      setWordsMessage("同じ言葉が既に在ります");
      return;
    }
    if (await saveWords([...words.words, word])) setWordDraft("");
  };

  const removeWord = (word: string) => {
    if (words) saveWords(words.words.filter((one) => one !== word));
  };

  return (
    <div className="pl-tabbed">
      {/* ヘッダ。題と、開いているタブの説明と、その他へ戻るボタン */}
      <div className="pl-title">
        <h2>脚本設定</h2>
        <p className="pl-title-lead">{TABS.find((one) => one.kind === kind)?.lead}</p>
        <button type="button" className="pl-back" onClick={onBack}>
          戻る
        </button>
      </div>
      <div className="pl-tabs pl-tabs-folder" role="tablist">
        {TABS.map((one) => (
          <button key={one.kind} type="button" role="tab" aria-selected={kind === one.kind} className={kind === one.kind ? "pl-tab pl-tab-on" : "pl-tab"} onClick={() => setKind(one.kind)}>
            {one.label}
          </button>
        ))}
      </div>

      <div className="pl-folder-body">
      {kind === "scenario" ? (
        <div>
          {scenario === null ? (
            <p className="pl-note">{message || "読み込み中・・・"}</p>
          ) : (
            <>
              <div className="pl-card">
                {ITEMS.map((item) => {
                  const value = Number(scenario[item.key] ?? 3);
                  return (
                    <div key={item.key} className="pl-sc-item">
                      <div className="pl-sc-name">{item.key}</div>
                      <div className="pl-sc-scale" role="radiogroup" aria-label={item.key}>
                        {LEVELS.map((lv) => (
                          <button
                            key={lv}
                            type="button"
                            role="radio"
                            aria-checked={lv === value}
                            aria-label={`${item.key} ${lv}`}
                            className="pl-sc-step"
                            onClick={() => change(item.key, lv)}
                            disabled={sending}
                          >
                            <i className={lv === value ? "pl-sc-dot pl-sc-dot-on" : "pl-sc-dot"} />
                          </button>
                        ))}
                      </div>
                      <div className="pl-sc-ends">
                        <span>{item.low}</span>
                        <span>{item.high}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="pl-card">
                <div className="pl-sc-head">
                  <b>テーマ</b>
                  <small>
                    {String(scenario["テーマ"] ?? "").length} / {THEME_MAX}
                  </small>
                </div>
                <input
                  className="pl-sc-input pl-sc-input-wide"
                  maxLength={THEME_MAX}
                  value={String(scenario["テーマ"] ?? "")}
                  onChange={(event) => change("テーマ", event.target.value)}
                  placeholder="例：ダークファンタジー"
                  disabled={sending}
                />
              </div>
              <div className="pl-sc-actions">
                <Button variant="primary" onClick={save} disabled={!changed || sending}>
                  {sending ? "保存しています" : "保存する"}
                </Button>
                <Button inline onClick={() => saved && setScenario(saved)} disabled={!changed || sending}>
                  元に戻す
                </Button>
              </div>
              {message ? <p className="pl-note">{message}</p> : null}
            </>
          )}
        </div>
      ) : kind === "rules" ? (
        <div>
          {rules === null ? (
            <p className="pl-note">{rulesMessage || "読み込み中・・・"}</p>
          ) : (
            <>
              <div className="pl-card pl-log-card">
                <button type="button" className="pl-log-head" aria-expanded={commonOpen} onClick={() => setCommonOpen((open) => !open)}>
                  <span>
                    <b>共通の規定</b>
                    <small>変更不可</small>
                  </span>
                  <span className="pl-log-arrow">{commonOpen ? "▲" : "▼"}</span>
                </button>
                {commonOpen ? (
                  <div className="pl-log-body">
                    {rules.common.map((section) => (
                      <div key={section.title} className="pl-sc-rules">
                        <h4>{section.title}</h4>
                        <ul>
                          {section.lines.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="pl-card">
                <div className="pl-sc-head">
                  <b>このキャラの追加の規定</b>
                  <small>
                    {rules.extra.length} / {rules.limits.count} 件
                  </small>
                </div>
                {rules.extra.length === 0 ? <p className="pl-note">追加の規定はありません。</p> : null}
                <ul className="pl-sc-list">
                  {rules.extra.map((line, index) => (
                    <li key={`${index}-${line}`}>
                      <span>{line}</span>
                      <button type="button" className="pl-sc-x" aria-label={`${line} を消す`} onClick={() => removeExtra(index)} disabled={rulesSending}>
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="pl-sc-add">
                  <textarea
                    className="pl-sc-input"
                    rows={2}
                    maxLength={rules.limits.length}
                    value={extraDraft}
                    onChange={(event) => setExtraDraft(event.target.value)}
                    placeholder="例：方言の台詞は標準語に直さない"
                    disabled={rulesSending}
                  />
                  <button type="button" className="pl-sc-add-btn" onClick={addExtra} disabled={rulesSending || extraDraft.trim() === "" || rules.extra.length >= rules.limits.count}>
                    足す
                  </button>
                </div>
                {rulesMessage ? <p className="pl-message">{rulesMessage}</p> : null}
              </div>
            </>
          )}
        </div>
      ) : (
        <div>
          {words === null ? (
            <p className="pl-note">{wordsMessage || "読み込み中・・・"}</p>
          ) : (
            <div className="pl-card">
              <div className="pl-sc-head">
                <b>禁句</b>
                <small>
                  {words.words.length} / {words.limits.count} 件
                </small>
              </div>
              {words.words.length === 0 ? <p className="pl-note">禁句はありません。</p> : null}
              <div className="pl-sc-chips">
                {words.words.map((word) => (
                  <span key={word} className="pl-sc-chip">
                    {word}
                    <button type="button" className="pl-sc-x" aria-label={`${word} を消す`} onClick={() => removeWord(word)} disabled={wordsSending}>
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="pl-sc-add">
                <input
                  className="pl-sc-input"
                  maxLength={words.limits.length}
                  value={wordDraft}
                  onChange={(event) => {
                    setWordDraft(event.target.value);
                    setWordsMessage("");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.nativeEvent.isComposing) addWord();
                  }}
                  placeholder="出したくない言葉"
                  disabled={wordsSending}
                />
                <button type="button" className="pl-sc-add-btn" onClick={addWord} disabled={wordsSending || wordDraft.trim() === "" || words.words.length >= words.limits.count}>
                  足す
                </button>
              </div>
              {wordsMessage ? <p className="pl-message">{wordsMessage}</p> : null}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
